/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Component: Ads
 * File     : Ads.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   Reusable React UI component: Ads, composed within pages and other components.
 *   Exports: ADS_CLIENT, ADS_ENABLED, ADS_TEST, AdSlot, AutoAds
 *   
 *
 * Dependencies:
 *   - react
 *
 * Usage:
 *   Import: import Ads from '@/components/layout/Ads';
 * -----------------------------------------------------------------------------
 */
import React, { useEffect } from 'react';

export const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS || 'false') === 'true';
export const ADS_CLIENT = process.env.REACT_APP_ADS_CLIENT || 'ca-pub-xxxxxxxxxxxxxxxx';
export const ADS_TEST = (process.env.REACT_APP_ADS_TEST || 'true') === 'true'; // default test on

export const AutoAds = () => {
  useEffect(() => {
    if (!ADS_ENABLED) return;
    const already = document.querySelector('script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]');
    if (!already) {
      const s = document.createElement('script');
      s.setAttribute('data-adsbygoogle', 'true');
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADS_CLIENT}`;
      s.crossOrigin = 'anonymous';
      document.head.appendChild(s);
    }
  }, []);
  return null;
};

export const AdSlot = ({ slot, style, format = 'auto', responsive = 'true' }) => {
  useEffect(() => {
    if (!ADS_ENABLED) return;
    try {
      // eslint-disable-next-line no-undef
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {}
  }, []);

  if (!ADS_ENABLED) {
    // Placeholder box to visualize ad space when ads disabled or misconfigured
    return <div style={{ width: '100%', minHeight: 90, background: 'rgba(255,255,255,0.06)', border: '1px dashed rgba(255,255,255,0.2)', borderRadius: 8 }} />;
  }

  if (!slot) {
    // No slot id provided; render placeholder for clarity
    return <div style={{ width: '100%', minHeight: 90, background: 'rgba(255,255,255,0.06)', border: '1px dashed rgba(255,255,255,0.2)', borderRadius: 8 }} />;
  }
  return (
    <ins
      className="adsbygoogle"
      style={style || { display: 'block' }}
      data-ad-client={ADS_CLIENT}
      data-ad-slot={slot}
      data-ad-format={format}
      data-full-width-responsive={responsive}
      data-adtest={ADS_TEST ? 'on' : undefined}
    />
  );
};


